import { useState } from "react";
import { Loader2, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronClosed } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEventStreams, useEvents } from "@/hooks/api";

const PAGE_SIZE = 50;

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function EventDataRow({ event }: { event: any }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-admin-slate-hover"
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell className="text-xs font-mono w-12">
          {expanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronClosed className="h-3 w-3" />
          )}
        </TableCell>
        <TableCell className="text-xs font-mono">{event.id}</TableCell>
        <TableCell>
          <Badge variant="outline" className="font-mono text-xs">
            {event.type}
          </Badge>
        </TableCell>
        <TableCell className="text-xs font-mono">
          {event.streamSeq ?? "-"}
        </TableCell>
        <TableCell className="text-xs font-mono text-admin-text-subtle max-w-[200px] truncate">
          {event.streamId ?? "-"}
        </TableCell>
        <TableCell className="text-xs whitespace-nowrap">
          {formatDate(event.createdAt)}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="p-0">
            <pre className="text-xs bg-admin-bg p-4 m-2 rounded overflow-auto max-h-80 whitespace-pre-wrap font-mono">
              {JSON.stringify(event.data, null, 2)}
            </pre>
            {event.metadata &&
              Object.keys(event.metadata).length > 0 && (
                <>
                  <p className="text-xs text-admin-text-secondary px-4 pt-2">
                    Metadata:
                  </p>
                  <pre className="text-xs bg-admin-bg p-4 m-2 mt-1 rounded overflow-auto max-h-40 whitespace-pre-wrap font-mono">
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                </>
              )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function EventsTab() {
  const [stream, setStream] = useState("");
  const [streamId, setStreamId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(0);

  const { data: streams, isLoading: streamsLoading } = useEventStreams();
  const events = useEvents({
    stream: stream || undefined,
    streamId: streamId.trim() || undefined,
    type: typeFilter.trim() || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="bg-admin-bg border border-admin-card-border rounded px-3 py-1.5 text-sm"
          value={stream}
          onChange={(e) => {
            setStream(e.target.value);
            setPage(0);
          }}
        >
          <option value="">Alle streams</option>
          {(streams ?? []).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <Input
          placeholder="Stream ID (f.eks. bruker-UUID)..."
          className="max-w-xs"
          value={streamId}
          onChange={(e) => setStreamId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setPage(0);
          }}
        />

        <Input
          placeholder="Event type..."
          className="max-w-[200px]"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setPage(0);
          }}
        />

        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(0)}
          disabled={events.isFetching}
        >
          {events.isFetching ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            "Sok"
          )}
        </Button>
      </div>

      {!stream && !streamId.trim() && (
        <p className="text-sm text-admin-text-subtle">
          Velg en stream eller skriv inn en stream ID for a lete.
        </p>
      )}

      {(stream || streamId.trim()) && (
        <Card>
          <CardContent className="pt-4">
            {events.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-16">ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-16">Seq</TableHead>
                      <TableHead>Stream ID</TableHead>
                      <TableHead>Tidspunkt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(events.data ?? []).map((event: any) => (
                      <EventDataRow key={event.id} event={event} />
                    ))}
                    {(events.data ?? []).length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-admin-text-secondary py-8"
                        >
                          Ingen events funnet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-admin-text-secondary">
                    Side {page + 1} — Viser {(events.data ?? []).length} events
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(events.data ?? []).length < PAGE_SIZE}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
